import { Fragment } from 'react';

import type { socialSettingsSection } from '../../../../app/_shared/marketing/landingContent';
import { withEmphasis } from './emphasisText';
import './socialSettings.css';

type SettingsContent = typeof socialSettingsSection;
type GlyphKind = SettingsContent['ladder'][number]['glyph'];
type Venue = SettingsContent['venues'][number];

type Props = {
  content: SettingsContent;
  /**
   * The four-setting roadmap ladder + Studio tease. OFF by default, and the homepage does not
   * turn it on: two of the four rungs are "On the horizon", and the block was the longest on the
   * page — roadmap lore spent before the reader has decided they want the product at all. The
   * markup and copy stay here so a launch can switch it back on in one place.
   */
  showRoadmap?: boolean;
};

function SettingGlyph({ kind }: { kind: GlyphKind }) {
  switch (kind) {
    case 'dice':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden>
          <rect x="4" y="4" width="16" height="16" rx="4" />
          <circle cx="9" cy="9" r="1.1" fill="currentColor" stroke="none" />
          <circle cx="15" cy="9" r="1.1" fill="currentColor" stroke="none" />
          <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
          <circle cx="9" cy="15" r="1.1" fill="currentColor" stroke="none" />
          <circle cx="15" cy="15" r="1.1" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'flame':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden>
          <path d="M12 3.5c2.6 3 3.7 5 3.7 7.6a3.7 3.7 0 1 1-7.4 0c0-1.4.6-2.6 1.5-3.5-.2 1.3.4 2.1 1.4 2.5 1-1.5.2-3.5-.7-4.4C11 6.8 11.5 5 12 3.5Z" />
          <path d="M5.5 20.5l5-2.5M18.5 20.5l-5-2.5" strokeLinecap="round" />
        </svg>
      );
    case 'spark':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden>
          <path d="M12 3.2l1.7 5.8 5.8 1.7-5.8 1.7L12 18.2l-1.7-5.8L4.5 10.7l5.8-1.7z" />
          <circle cx="18.5" cy="5.5" r="1.2" />
        </svg>
      );
    case 'night':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden>
          <path d="M18.5 12.5A6.5 6.5 0 1 1 11 5a5.1 5.1 0 0 0 7.5 7.5Z" />
          <path
            d="M17 4.5l.5 1.6 1.6.5-1.6.5L17 8.7l-.5-1.6-1.6-.5 1.6-.5z"
            fill="currentColor"
            stroke="none"
          />
        </svg>
      );
    default:
      return null;
  }
}

/**
 * One venue: what it is, the two activities that show it best (see the note on `venues` in
 * landingContent — the full roster was the page's densest block of product nouns), and a real
 * prompt from it.
 *
 * Was hardcoded to Campfire — its class names, its amber, and its single row of bare activity
 * chips. Game Night got a "coming soon" teaser instead, describing games that were never built.
 * Both venues render through this now, and the accent comes from a CSS custom property set by the
 * `--<id>` modifier, so adding Studio later is a content entry and four lines of colour.
 */
function VenuePanel({ venue }: { venue: Venue }) {
  return (
    <article className={`eb-set__venue eb-set__venue--${venue.id}`}>
      <div className="eb-set__venueCopy">
        <span className="eb-set__chip">
          <span className="eb-set__liveDot" aria-hidden />
          {venue.label} · {venue.status}
        </span>
        <h3 className="ml-heading-serif eb-set__venueTitle">
          <span className="eb-set__venueGlyph" aria-hidden>
            <SettingGlyph kind={venue.glyph} />
          </span>
          {venue.title}
        </h3>
        <p className="eb-set__venueBody">{venue.body}</p>
        <p className="eb-set__threads">{venue.threads}</p>
      </div>

      <div className="eb-set__venueRight">
        <ul className="eb-set__acts">
          {venue.activities.map((activity) => (
            <li key={activity.name} className="eb-set__act">
              <span className="eb-set__actWhen">{activity.when}</span>
              <span className="eb-set__actName">{activity.name}</span>
              <span className="eb-set__actBody">{activity.body}</span>
            </li>
          ))}
        </ul>

        <div className="eb-set__prompt">
          <p className="eb-set__promptLabel">{venue.promptLabel}</p>
          <p className="eb-set__promptText">{venue.prompt}</p>
          <div className="eb-set__promptReplies">
            <div className="eb-set__avatars" aria-hidden>
              <span className="eb-set__av eb-set__av--1" />
              <span className="eb-set__av eb-set__av--2" />
              <span className="eb-set__av eb-set__av--3" />
              <span className="eb-set__av eb-set__av--4" />
            </div>
            <span className="eb-set__repliesNote">{venue.repliesNote}</span>
          </div>
        </div>
      </div>
    </article>
  );
}

export function SocialSettingsSection({ content, showRoadmap = false }: Props) {
  const {
    eyebrow,
    title,
    lead,
    reframe,
    venues,
    ladderEyebrow,
    ladderTitle,
    ladderLead,
    arc,
    ladder,
    tease,
  } = content;

  return (
    <div className="eb-set">
      <div className="eb-set__inner">
        <header className="eb-set__head">
          <p className="eb-eyebrow">{eyebrow}</p>
          <h2 className="ml-heading-serif eb-set__title">{title}</h2>
          <p className="eb-set__lead">{lead}</p>
          <p className="eb-set__reframe">{withEmphasis(reframe)}</p>
        </header>

        <div className="eb-set__venues">
          {venues.map((venue) => (
            <VenuePanel key={venue.id} venue={venue} />
          ))}
        </div>
      </div>

      {showRoadmap ? (
        <div className="eb-set__inner">
          <header className="eb-set__head eb-set__head--center">
            <p className="eb-eyebrow">{ladderEyebrow}</p>
            <h2 className="ml-heading-serif eb-set__title">{ladderTitle}</h2>
            <p className="eb-set__lead">{ladderLead}</p>
            <p className="eb-set__arc">
              {arc.map((word, i) => (
                <Fragment key={word}>
                  {i > 0 ? (
                    <span className="eb-set__arcSep" aria-hidden>
                      →
                    </span>
                  ) : null}
                  <b>{word}</b>
                </Fragment>
              ))}
            </p>
          </header>

          <div className="eb-set__ladder">
            {ladder.map((item) => (
              <article key={item.id} className={`eb-set__card eb-set__card--${item.state}`}>
                <span className="eb-set__glyph" aria-hidden>
                  <SettingGlyph kind={item.glyph} />
                </span>
                <span className="eb-set__temp">{item.temperature}</span>
                <h3 className="ml-heading-serif eb-set__cardTitle">{item.title}</h3>
                <p className="eb-set__cardBody">{item.body}</p>
                <span className="eb-set__status">{item.status}</span>
              </article>
            ))}
          </div>

          <aside className="eb-set__tease">
            <span className="eb-set__teaseGlyph" aria-hidden>
              <SettingGlyph kind={tease.glyph} />
            </span>
            <div className="eb-set__teaseCopy">
              <p className="eb-eyebrow">{tease.eyebrow}</p>
              <h3 className="ml-heading-serif eb-set__teaseTitle">{tease.title}</h3>
              <p className="eb-set__teaseBody">{tease.body}</p>
            </div>
            <span className="eb-set__soon">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden>
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7.5v5l3 2" strokeLinecap="round" />
              </svg>
              {tease.soon}
            </span>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
